"use client";

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { useCallback, useRef, useState } from "react";

import { safePlayVideo } from "@/lib/safe-video-play";

gsap.registerPlugin(ScrollTrigger);

export function CreativeCampaignSection() {
  const scope = useRef<HTMLElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const maskRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);

  const moveCursor = useCallback((clientX: number, clientY: number) => {
    const el = cursorRef.current;
    if (!el) return;
    el.style.left = `${clientX}px`;
    el.style.top = `${clientY}px`;
  }, []);

  const handleVideoClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (muted) {
      video.currentTime = 0;
      video.muted = false;
      setMuted(false);
      safePlayVideo(video);
    } else {
      video.muted = true;
      setMuted(true);
    }
  }, [muted]);

  useGSAP(() => {
    const viewport = viewportRef.current;
    const mask = maskRef.current;
    const video = videoRef.current;
    if (!mask || !viewport || !video) return;

    // 1. 创建 matchMedia 实例
    const mm = gsap.matchMedia();

    // 2. 使用条件语法：同时处理屏幕尺寸和减弱动画
    mm.add({
      // 这里的 key 你可以随便起名
      reduceMotion: "(prefers-reduced-motion: reduce)",
      isDesktop: "(min-width: 1024px)",
      isMobile: "(max-width: 1023px)"
    }, (context) => {
      // 获取当前的匹配状态
      const { reduceMotion } = context?.conditions as { reduceMotion: boolean };

      if (reduceMotion) {
        gsap.set(mask, {
          width: "100%",
          height: "100%",
          borderRadius: "1.9rem",
        });
      } else {
        // 正常的动画逻辑
        gsap.fromTo(mask,
          { width: 500, height: 500, borderRadius: "100%" },
          {
            width: "100%",
            height: "100%",
            borderRadius: "1.9rem",
            ease: "none",
            scrollTrigger: {
              trigger: viewport,
              start: "center 90%",
              end: "center 70%",
              scrub: 1.3,
              invalidateOnRefresh: true, // 核心：窗口变大小时自动重新计算 100%
            },
          }
        );
      }

    });

    // 同步 viewport 的宽度和高度
    const syncVars = () => {
      viewport.style.setProperty("--screen-w", `${viewport.offsetWidth}px`);
      viewport.style.setProperty("--screen-h", `${viewport.offsetHeight}px`);
    };
    syncVars();
    window.addEventListener("resize", syncVars);

    const playVideo = () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      safePlayVideo(video);
    };
    const pauseVideo = () => {
      video.pause();
    };

    const videoScrollTrigger = ScrollTrigger.create({
      trigger: viewport,
      start: "top 80%",
      end: "bottom 20%",
      onToggle: (self) => {
        if (self.isActive) playVideo();
        else pauseVideo();
      },
    });

    return () => {
      window.removeEventListener("resize", syncVars);
      video.pause();
      videoScrollTrigger.kill();
      mm.revert(); // 确保 mm 也被销毁
    };
  }, { scope, dependencies: [] });

  return (
    <section
      ref={scope}
      className="relative min-h-[1200px] overflow-visible bg-[#1b2631] pt-16 font-sans text-white md:pt-20 pb-20"
      aria-labelledby="creative-campaign-heading"
    >
      <div className="site-content pt-12 pb-0 md:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[550px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#8fa8bf] md:text-base">
              Creative social media campaigns
            </p>
            <h2
              id="creative-campaign-heading"
              className="mt-4 text-4xl leading-[0.8em] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Stop the endless scrolling and{" "}
              <span className="text-[#ff5c00]">own the moment</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className="text-4xl font-medium leading-none text-[#ff5c00] md:text-5xl">
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-white/90" />
            <p className="mt-6 text-base leading-relaxed text-white/95 md:text-lg">
              Great creativity is the only thing that breaks the hypnosis of
              mindless scrolling. We build social campaigns that earn
              attention, spark conversation, and turn passive feeds into
              moments people remember — and choose your brand in.
            </p>
          </div>
        </div>
      </div>

      <div className="site-content relative mt-6 sm:h-0 md:mt-16">
        <div className="relative left-0 right-0 top-0 z-10 w-full md:left-1/2 md:right-auto md:w-[min(30rem,76vw)] md:-translate-x-1/2 lg:w-[min(42rem,72vw)]">
          <div className="max-md:rounded-2xl max-md:p-0 sm:max-md:rounded-3xl sm:max-md:p-2.5 rounded-[2.5rem] p-2.5 md:rounded-[2.75rem] md:p-3 lg:rounded-[3rem] lg:p-3.5">
            <div
              ref={viewportRef}
              className="relative aspect-9/17 w-full overflow-hidden max-md:rounded-[1.35rem] rounded-[1.85rem] md:rounded-[2.125rem] lg:rounded-4xl"
            >
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  ref={maskRef}
                  className="flex items-center justify-center overflow-hidden will-change-[width,height,border-radius]"
                >
                  <div
                    className="relative shrink-0 [&_video]:pointer-events-none"
                    style={{
                      width: "var(--screen-w, 100%)",
                      height: "var(--screen-h, 100%)",
                    }}
                  >
                    <video
                      ref={videoRef}
                      className="absolute inset-0 h-full w-full object-cover"
                      src="/images/cameron-law-new-video.mp4"
                      loop
                      muted={muted}
                      playsInline
                      preload="metadata"
                      aria-label="Creative campaign video"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                aria-label={muted ? "Play video with sound" : "Mute video"}
                className={
                  muted
                    ? "absolute inset-0 z-10 touch-manipulation border-0 bg-transparent p-0 md:cursor-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ff5c00]"
                    : "absolute inset-0 z-10 touch-manipulation border-0 bg-transparent p-0 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#ff5c00]"
                }
                onMouseEnter={(e) => {
                  setIsHoveringVideo(true);
                  moveCursor(e.clientX, e.clientY);
                }}
                onMouseLeave={() => setIsHoveringVideo(false)}
                onMouseMove={(e) => moveCursor(e.clientX, e.clientY)}
                onClick={handleVideoClick}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        ref={cursorRef}
        className={`pointer-events-none fixed left-0 top-0 z-100 h-24 w-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#ff5c00] shadow-lg max-md:hidden ${muted && isHoveringVideo ? "flex" : "hidden"}`}
        aria-hidden
      >
        <svg
          className="size-10 text-white"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
      </div>
    </section>
  );
}
